import {
  Lesson,
  LessonSession,
  ApiResponse,
  PaginatedResponse,
  LessonCreateRequest,
  CampSeriesCreateRequest,
  LessonOverride,
  ProRataPreviewResponse,
} from '@court-sync/shared';
import type { PaginationState } from '../components/ui/PaginatedTable';
import { apiGet, apiPost, apiPut, apiDelete, publicApiGet, apiClient } from './apiClient';

interface LessonFilters {
  academyId?: string;
  termId?: string;
  coachId?: string;
  dayOfWeek?: number;
  available?: boolean;
  lessonType?: 'group' | 'private' | 'camp';
  activeStatus?: 'active' | 'inactive';
}

export interface PublicLessonBrowseParams {
  page: number;
  limit: number;
  lessonType?: 'group' | 'camp';
  termId?: string;
  locationId?: string;
  programId?: string;
  ageGroupId?: string;
  sportId?: string;
  search?: string;
  skillLevel?: string;
  ageGroup?: string;
  dayOfWeek?: number;
  maxPrice?: number;
}

export const PUBLIC_LESSON_PAGE_SIZE = 12;

/** Matches GET /lessons/:id/enrollments response rows */
export interface LessonEnrollmentRow {
  bookingId?: string;
  makeupSessionReservationId?: string;
  enrollmentSource?: 'booking' | 'makeup_session';
  studentId: string;
  studentName: string;
  studentEmail: string;
  parentId?: string;
  parentName?: string;
  parentEmail?: string;
  status: string;
  bookingDate: string;
  bookingScope?: 'term' | 'session' | 'package';
  isMakeupBooking?: boolean;
  isReportedAbsent?: boolean;
  isSelf?: boolean;
  isAdultStudent?: boolean;
  isManual?: boolean;
  inviteEmail?: string | null;
  sessionDateYmd?: string;
  attendanceStatus?: 'scheduled' | 'attended' | 'missed' | 'cancelled';
}

/** Query params for GET /lessons with pagination (must match backend `packages/backend/src/routes/private/lesson.ts`) */
export interface LessonListQueryFilters {
  termId?: string;
  coachId?: string;
  coachIds?: string;
  lessonType?: 'group' | 'private' | 'camp';
  skillLevel?: string;
  ageGroup?: string;
  courtId?: string;
  activeStatus?: 'active' | 'inactive';
  featuredStatus?: 'featured' | 'not-featured';
  locationId?: string;
  year?: string;
  overlapFrom?: string;
  overlapTo?: string;
  sortBy?: 'name' | 'type' | 'price' | 'day' | 'time' | 'enrollment' | 'term';
  sortOrder?: 'asc' | 'desc';
}

export const lessonService = {
  async getLessons(filters?: LessonFilters): Promise<ApiResponse<Lesson[]>> {
    return apiGet<Lesson[]>('/lessons', { params: filters });
  },

  async getAvailableLessons(academyId: string, termId: string): Promise<ApiResponse<Lesson[]>> {
    return apiGet<Lesson[]>('/lessons/available', {
      params: { academyId, termId },
    });
  },

  async getMakeupSessionsForAcademy(
    academyId: string
  ): Promise<ApiResponse<Array<{ lessonId: string; date: string }>>> {
    return apiGet<Array<{ lessonId: string; date: string }>>('/lessons/makeup-sessions', { params: { academyId } });
  },

  async getSchedulePendingBookings(
    academyId: string
  ): Promise<ApiResponse<Array<{ lessonId: string; sessionDate?: string }>>> {
    return apiGet<Array<{ lessonId: string; sessionDate?: string }>>('/lessons/schedule-pending-bookings', { params: { academyId } });
  },

  async getReportedAbsencesForAcademy(
    academyId: string
  ): Promise<ApiResponse<Array<{ lessonId: string; date: string; bookingId: string }>>> {
    return apiGet<Array<{ lessonId: string; date: string; bookingId: string }>>('/lessons/reported-absences', { params: { academyId } });
  },

  async getLessonEnrollments(
    lessonId: string,
    params: { academyId: string; sessionDate?: string; rosterScope?: 'term' }
  ): Promise<ApiResponse<LessonEnrollmentRow[]>> {
    return apiGet<LessonEnrollmentRow[]>(`/lessons/${lessonId}/enrollments`, {
      params: {
        academyId: params.academyId,
        sessionDate: params.sessionDate,
        rosterScope: params.rosterScope,
      },
    });
  },

  async markSessionAttendance(
    lessonId: string,
    params: {
      academyId: string;
      sessionDate: string;
      sessionId?: string;
      records: Array<{
        bookingId: string;
        status: 'scheduled' | 'attended' | 'missed' | 'cancelled';
        notes?: string;
      }>;
    }
  ): Promise<ApiResponse<{ count: number }>> {
    return apiPost<{ count: number }>(`/lessons/${lessonId}/attendance`, {
      academyId: params.academyId,
      sessionDate: params.sessionDate,
      ...(params.sessionId ? { sessionId: params.sessionId } : {}),
      records: params.records,
    });
  },

  async getLesson(id: string): Promise<ApiResponse<Lesson>> {
    return apiGet<Lesson>(`/lessons/${id}`);
  },

  async getLessonSessions(lessonId: string): Promise<LessonSession[]> {
    const response = await apiGet<LessonSession[]>(`/lessons/${lessonId}/sessions`);
    if (!response.success || !response.data) return [];
    return response.data;
  },

  async createLesson(lessonData: LessonCreateRequest): Promise<ApiResponse<{ id: string }>> {
    return apiPost<{ id: string }>('/lessons', lessonData);
  },

  async createCampSeries(lessonData: CampSeriesCreateRequest): Promise<ApiResponse<{ ids: string[] }>> {
    return apiPost<{ ids: string[] }>('/lessons/camp-series', lessonData);
  },

  async updateLesson(id: string, lessonData: Partial<Omit<Lesson, 'id' | 'createdAt' | 'updatedAt' | 'currentEnrollment'>>): Promise<ApiResponse> {
    return apiPut(`/lessons/${id}`, lessonData);
  },

  async deleteLesson(id: string): Promise<ApiResponse> {
    return apiDelete(`/lessons/${id}`);
  },

  async getPublicLessons(academyId: string): Promise<Lesson[]> {
    const response = await publicApiGet<Lesson[]>(`/public/lessons/academy/${academyId}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch lessons');
  },

  async getPublicLessonsBrowse(
    academyId: string,
    params: PublicLessonBrowseParams
  ): Promise<{ data: Lesson[]; pagination: PaginationState }> {
    const response = await publicApiGet<Lesson[]>(
      `/public/lessons/academy/${academyId}/browse`,
      { params }
    ) as PaginatedResponse<Lesson>;
    if (response.success && response.data && response.pagination) {
      return { data: response.data, pagination: response.pagination };
    }
    throw new Error(response.error || 'Failed to fetch lessons');
  },

  async getLessonById(id: string): Promise<Lesson> {
    const response = await publicApiGet<Lesson>(`/public/lessons/${id}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch lesson');
  },

  async getProRataPreview(lessonId: string, academyId: string): Promise<ProRataPreviewResponse> {
    const response = await publicApiGet<ProRataPreviewResponse>(`/public/lessons/${lessonId}/pro-rata-preview`, {
      params: { academyId },
    });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch pro-rata preview');
  },

  async getPublicLessonOverrides(lessonId: string, academyId: string): Promise<LessonOverride[]> {
    const response = await publicApiGet<{ overrides?: LessonOverride[] }>(`/public/lessons/${lessonId}/overrides`, {
      params: { academyId },
    });
    if (response.success && response.data) {
      return response.data.overrides ?? [];
    }
    throw new Error(response.error || 'Failed to fetch lesson overrides');
  },

  async getLessonsPaginated(
    academyId: string,
    page: number,
    limit: number,
    filters?: LessonListQueryFilters
  ): Promise<{ data: Lesson[]; pagination: PaginationState }> {
    const response = await apiClient.get<{ data: Lesson[]; pagination: PaginationState }>('/lessons', {
      params: { academyId, page, limit, ...filters },
    });
    return response.data;
  },

  async addLessonCoach(
    lessonId: string,
    payload: { academyId: string; coachId: string; notifyCoachEmail?: boolean }
  ): Promise<ApiResponse> {
    return apiPost(`/lessons/${lessonId}/coaches`, payload);
  },

  async shareLessonLink(
    lessonId: string,
    payload: { academyId: string; studentIds: string[] }
  ): Promise<ApiResponse<{ sent: number; skipped: number; skippedNoEmail: string[] }>> {
    return apiPost(`/lessons/${lessonId}/share-link`, payload);
  },
};
